import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Eye, EyeOff, Search, Loader2, X, Image, Tag, User, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Styles ──────────────────────────────────────────────────────────────── */
const S = {
  wrap:  { fontFamily: "'Satoshi', sans-serif" },

  /* Toolbar */
  toolbar: { display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '22px', flexWrap: 'wrap' },
  searchBox: { display: 'flex', alignItems: 'center', gap: '8px', background: '#f8f7f5', border: '1.5px solid #ece9e4', borderRadius: '11px', padding: '0 14px', flex: 1, minWidth: '200px' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '13px', color: '#111', padding: '10px 0', background: 'transparent', fontFamily: "'Satoshi', sans-serif" },

  addBtn: { display: 'flex', alignItems: 'center', gap: '7px', background: '#ff5a00', border: 'none', borderRadius: '11px', padding: '10px 20px', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: "'Satoshi', sans-serif", transition: 'background 0.15s' },

  /* Blog row */
  list:  { display: 'flex', flexDirection: 'column', gap: '10px' },
  row:   { background: '#fff', border: '1.5px solid #ece9e4', borderRadius: '16px', overflow: 'hidden', display: 'flex', alignItems: 'stretch', transition: 'border-color 0.2s' },
  rowImg:{ width: '100px', minHeight: '80px', flexShrink: 0, overflow: 'hidden', background: 'linear-gradient(135deg,rgba(255,90,0,0.07),rgba(180,180,180,0.07))' },
  rowImgEl: { width: '100%', height: '100%', objectFit: 'cover' },
  rowBody:  { flex: 1, padding: '14px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 },
  rowTitle: { fontSize: '14px', fontWeight: 700, color: '#111', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  rowMeta:  { fontSize: '12px', color: '#aaa', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' },
  rowActions:{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0 16px', flexShrink: 0 },

  actionBtn: (color) => ({
    width: '34px', height: '34px', borderRadius: '10px', border: 'none',
    background: `rgba(${color},0.08)`, color: `rgb(${color})`,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.15s', fontFamily: 'inherit',
  }),

  /* Empty */
  empty: { textAlign: 'center', padding: '56px 24px' },
  emptyIcon:  { fontSize: '44px', marginBottom: '12px' },
  emptyTitle: { fontSize: '18px', fontWeight: 700, color: '#111', marginBottom: '6px' },
  emptySub:   { fontSize: '13px', color: '#aaa' },

  /* ── Modal form ── */
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '32px 16px' },
  modal: { background: '#fff', borderRadius: '24px', width: '100%', maxWidth: '760px', boxShadow: '0 24px 80px rgba(0,0,0,0.18)', position: 'relative', overflow: 'hidden' },
  modalHeader: { padding: '28px 32px 20px', borderBottom: '1px solid #f0ede8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  modalTitle: { fontSize: '20px', fontWeight: 700, color: '#111' },
  modalClose: { width: '36px', height: '36px', borderRadius: '10px', border: 'none', background: '#f5f4f0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' },
  modalBody: { padding: '28px 32px 32px', overflowY: 'auto', maxHeight: '78vh' },

  formGroup: { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '18px' },
  formLabel: { fontSize: '11px', fontWeight: 700, color: '#aaa', letterSpacing: '0.08em', textTransform: 'uppercase' },
  formInput: { background: '#f8f7f5', border: '1.5px solid #ece9e4', borderRadius: '11px', padding: '11px 14px', fontSize: '14px', color: '#111', outline: 'none', fontFamily: "'Satoshi', sans-serif", width: '100%', boxSizing: 'border-box', transition: 'border-color 0.2s' },
  formTextarea: { background: '#f8f7f5', border: '1.5px solid #ece9e4', borderRadius: '11px', padding: '11px 14px', fontSize: '14px', color: '#111', outline: 'none', fontFamily: "'Satoshi', sans-serif", width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: '100px', lineHeight: 1.7, transition: 'border-color 0.2s' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },

  /* Image upload area */
  imgUpload: { background: '#f8f7f5', border: '2px dashed #e0ddd8', borderRadius: '14px', padding: '24px', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s' },
  imgPreview: { width: '100%', height: '180px', objectFit: 'cover', borderRadius: '10px', marginTop: '10px' },

  /* Tags input */
  tagsWrap: { display: 'flex', gap: '7px', flexWrap: 'wrap', background: '#f8f7f5', border: '1.5px solid #ece9e4', borderRadius: '11px', padding: '8px 12px', minHeight: '44px', cursor: 'text', alignItems: 'center' },
  tagPill:  { display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(255,90,0,0.1)', border: '1px solid rgba(255,90,0,0.25)', borderRadius: '6px', padding: '2px 8px', fontSize: '12px', fontWeight: 600, color: '#ff5a00' },
  tagInput: { border: 'none', outline: 'none', background: 'transparent', fontSize: '13px', color: '#111', fontFamily: "'Satoshi', sans-serif", minWidth: '80px', flex: 1 },

  /* Content area notice */
  contentNotice: { background: 'rgba(255,90,0,0.05)', border: '1px solid rgba(255,90,0,0.15)', borderRadius: '10px', padding: '10px 14px', fontSize: '12px', color: '#888', marginBottom: '8px', lineHeight: 1.6 },

  saveBtn: { width: '100%', background: '#ff5a00', border: 'none', borderRadius: '12px', padding: '14px', fontSize: '14px', fontWeight: 700, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '8px', fontFamily: "'Satoshi', sans-serif", transition: 'background 0.15s' },

  publishToggle: (on) => ({
    display: 'flex', alignItems: 'center', gap: '10px',
    background: on ? 'rgba(52,211,153,0.07)' : '#f5f4f0',
    border: `1.5px solid ${on ? 'rgba(52,211,153,0.3)' : '#e0ddd8'}`,
    borderRadius: '11px', padding: '10px 16px', cursor: 'pointer',
    fontSize: '13px', fontWeight: 600, color: on ? '#059669' : '#888',
    transition: 'all 0.15s', fontFamily: "'Satoshi', sans-serif'",
  }),
  toggleDot: (on) => ({
    width: '18px', height: '18px', borderRadius: '50%',
    background: on ? '#34d399' : '#d1d5db',
    transition: 'background 0.2s', flexShrink: 0,
  }),
};

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}
function fileToBase64(file) {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload  = () => res(reader.result);
    reader.onerror = () => rej(new Error('Read failed'));
    reader.readAsDataURL(file);
  });
}

/* ─── Default form state ──────────────────────────────────────────────────── */
const EMPTY_FORM = {
  title: '', slug: '', cover_image: '', description: '',
  content: '', written_by: '', author_role: 'X7 Autoz Team',
  author_avatar: '', tags: [], published: true,
};

/* ══════════════════════════════════════════════════════════════════════════════
   BLOG MANAGER COMPONENT  (embed in AdminDashboard as a TabsContent)
══════════════════════════════════════════════════════════════════════════════ */
const BlogsManager = ({ token }) => {
  const [blogs, setBlogs]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editBlog, setEditBlog]   = useState(null);   // null = create
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [tagInput, setTagInput]   = useState('');
  const [coverPreview, setCoverPreview] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');

  const coverInputRef  = useRef();
  const avatarInputRef = useRef();
  const tagInputRef    = useRef();

  useEffect(() => { fetchBlogs(); }, [search]);

  /* ── Fetch ── */
  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 50, skip: 0 });
      if (search) params.set('search', search);
      const res = await axios.get(`${API}/superadmin/blogs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlogs(res.data.blogs || []);
      setTotal(res.data.total  || 0);
    } catch {
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  /* ── Open modal ── */
  const openCreate = () => {
    setEditBlog(null);
    setForm(EMPTY_FORM);
    setCoverPreview('');
    setAvatarPreview('');
    setTagInput('');
    setShowModal(true);
  };

  const openEdit = async (blog) => {
    // fetch full blog (with content)
    try {
      const res = await axios.get(`${API}/superadmin/blogs/${blog.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const b = res.data.blog;
      setEditBlog(b);
      setForm({
        title:        b.title        || '',
        slug:         b.slug         || '',
        cover_image:  b.cover_image  || '',
        description:  b.description  || '',
        content:      b.content      || '',
        written_by:   b.written_by   || '',
        author_role:  b.author_role  || 'X7 Autoz Team',
        author_avatar:b.author_avatar|| '',
        tags:         b.tags         || [],
        published:    b.published !== false,
      });
      setCoverPreview(b.cover_image   || '');
      setAvatarPreview(b.author_avatar || '');
      setTagInput('');
      setShowModal(true);
    } catch {
      toast.error('Failed to load blog');
    }
  };

  /* ── Image picks ── */
  const pickCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const b64 = await fileToBase64(file);
    setForm(f => ({ ...f, cover_image: b64 }));
    setCoverPreview(b64);
  };

  const pickAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const b64 = await fileToBase64(file);
    setForm(f => ({ ...f, author_avatar: b64 }));
    setAvatarPreview(b64);
  };

  /* ── Tags ── */
  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t)) setForm(f => ({ ...f, tags: [...f.tags, t] }));
    setTagInput('');
    tagInputRef.current?.focus();
  };
  const removeTag = (t) => setForm(f => ({ ...f, tags: f.tags.filter(x => x !== t) }));
  const handleTagKey = (e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); } };

  /* ── Save ── */
  const handleSave = async () => {
    if (!form.title.trim())       { toast.error('Title is required');       return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (!form.content.trim())     { toast.error('Content is required');     return; }
    if (!form.written_by.trim())  { toast.error('Author name is required'); return; }
    if (!form.cover_image)        { toast.error('Cover image is required'); return; }

    setSaving(true);
    try {
      if (editBlog) {
        await axios.put(`${API}/superadmin/blogs/${editBlog.id}`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Blog updated!');
      } else {
        await axios.post(`${API}/superadmin/blogs`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Blog published!');
      }
      setShowModal(false);
      fetchBlogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  /* ── Toggle publish ── */
  const handleToggle = async (blog) => {
    try {
      const res = await axios.put(
        `${API}/superadmin/blogs/${blog.id}/toggle-publish`, {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(res.data.message);
      fetchBlogs();
    } catch {
      toast.error('Failed to toggle');
    }
  };

  /* ── Delete ── */
  const handleDelete = async (blog) => {
    if (!window.confirm(`Delete "${blog.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/superadmin/blogs/${blog.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Blog deleted');
      fetchBlogs();
    } catch {
      toast.error('Failed to delete');
    }
  };

  /* ─── render ─────────────────────────────────────────────────────────────── */
  return (
    <div style={S.wrap}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Satoshi:wght@400;500;700&display=swap');
        .bm-add-btn:hover { background: #e04e00 !important; }
        .bm-row:hover { border-color: #ff5a00 !important; }
        .bm-save:hover:not(:disabled) { background: #e04e00 !important; }
        .bm-save:disabled { opacity: 0.55; cursor: not-allowed; }
        .bm-action-orange:hover { background: rgba(255,90,0,0.16) !important; }
        .bm-action-red:hover    { background: rgba(239,68,68,0.16) !important; }
        .bm-img-upload:hover { border-color: #ff5a00 !important; }
        .bm-form-input:focus { border-color: #ff5a00 !important; background: #fff !important; }
      `}</style>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '17px', fontWeight: 700, color: '#111', marginBottom: '2px' }}>Blog Articles</h2>
          <p style={{ fontSize: '12px', color: '#aaa' }}>{total} article{total !== 1 ? 's' : ''} total</p>
        </div>
        <button className="bm-add-btn" style={S.addBtn} onClick={openCreate}>
          <Plus size={15} /> New Article
        </button>
      </div>

      {/* Search */}
      <div style={{ ...S.searchBox, marginBottom: '20px' }}>
        <Search size={14} color="#bbb" />
        <input
          className="bm-form-input"
          style={{ ...S.searchInput }}
          placeholder="Search blogs..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', display: 'flex' }} onClick={() => setSearch('')}><X size={14} /></button>}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#ff5a00' }} />
        </div>
      )}

      {/* Empty */}
      {!loading && blogs.length === 0 && (
        <div style={S.empty}>
          <div style={S.emptyIcon}>📝</div>
          <h3 style={S.emptyTitle}>{search ? 'No results' : 'No articles yet'}</h3>
          <p style={S.emptySub}>{search ? 'Try a different search.' : 'Click "New Article" to publish your first blog post.'}</p>
        </div>
      )}

      {/* List */}
      {!loading && blogs.length > 0 && (
        <div style={S.list}>
          {blogs.map((blog) => (
            <div key={blog.id} className="bm-row" style={S.row}>
              {/* Thumbnail */}
              <div style={S.rowImg}>
                {blog.cover_image
                  ? <img src={blog.cover_image} alt={blog.title} style={S.rowImgEl} />
                  : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px' }}>✍️</div>
                }
              </div>

              {/* Body */}
              <div style={S.rowBody}>
                <p style={S.rowTitle}>{blog.title}</p>
                <div style={S.rowMeta}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <User size={11} /> {blog.written_by}
                  </span>
                  <span>{formatDate(blog.created_at)}</span>
                  {blog.tags?.length > 0 && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Tag size={10} /> {blog.tags.slice(0, 2).join(', ')}
                    </span>
                  )}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: blog.published ? 'rgba(52,211,153,0.1)' : '#f5f4f0', color: blog.published ? '#059669' : '#aaa', borderRadius: '6px', padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>
                    {blog.published ? '● Live' : '○ Draft'}
                  </span>
                  {blog.views > 0 && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Eye size={10} /> {blog.views}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div style={S.rowActions}>
                <button
                  className="bm-action-orange"
                  style={S.actionBtn('255,90,0')}
                  onClick={() => handleToggle(blog)}
                  title={blog.published ? 'Unpublish' : 'Publish'}
                >
                  {blog.published ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button
                  className="bm-action-orange"
                  style={S.actionBtn('255,90,0')}
                  onClick={() => openEdit(blog)}
                  title="Edit"
                >
                  <Edit size={14} />
                </button>
                <button
                  className="bm-action-red"
                  style={S.actionBtn('239,68,68')}
                  onClick={() => handleDelete(blog)}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── Create / Edit Modal ──────────────────────────────────────────── */}
      {showModal && (
        <div style={S.modalOverlay} onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div style={S.modal}>
            {/* Header */}
            <div style={S.modalHeader}>
              <h3 style={S.modalTitle}>{editBlog ? 'Edit Article' : 'New Article'}</h3>
              <button style={S.modalClose} onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>

            {/* Body */}
            <div style={S.modalBody}>

              {/* Cover image */}
              <div style={S.formGroup}>
                <label style={S.formLabel}>Cover Image *</label>
                <input ref={coverInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={pickCover} />
                <div className="bm-img-upload" style={S.imgUpload} onClick={() => coverInputRef.current?.click()}>
                  {coverPreview
                    ? <img src={coverPreview} alt="cover" style={S.imgPreview} />
                    : <>
                        <Image size={28} color="#bbb" style={{ marginBottom: '8px' }} />
                        <p style={{ fontSize: '13px', color: '#aaa', margin: 0 }}>Click to upload cover image</p>
                        <p style={{ fontSize: '11px', color: '#ccc', margin: '4px 0 0' }}>JPG, PNG, WEBP recommended</p>
                      </>
                  }
                </div>
                {coverPreview && (
                  <button
                    onClick={() => { setForm(f => ({ ...f, cover_image: '' })); setCoverPreview(''); }}
                    style={{ fontSize: '11px', color: '#ff5a00', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', marginTop: '4px' }}
                  >✕ Remove image</button>
                )}
              </div>

              {/* Title */}
              <div style={S.formGroup}>
                <label style={S.formLabel}>Title *</label>
                <input
                  className="bm-form-input"
                  style={S.formInput}
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. How to Get the Best Price for Your Used Car"
                />
              </div>

              {/* Author row */}
              <div style={S.formRow}>
                <div style={S.formGroup}>
                  <label style={S.formLabel}>Written By *</label>
                  <input
                    className="bm-form-input"
                    style={S.formInput}
                    value={form.written_by}
                    onChange={e => setForm(f => ({ ...f, written_by: e.target.value }))}
                    placeholder="e.g. Rahul Sharma"
                  />
                </div>
                <div style={S.formGroup}>
                  <label style={S.formLabel}>Author Role</label>
                  <input
                    className="bm-form-input"
                    style={S.formInput}
                    value={form.author_role}
                    onChange={e => setForm(f => ({ ...f, author_role: e.target.value }))}
                    placeholder="e.g. X7 Autoz Team"
                  />
                </div>
              </div>

              {/* Author avatar */}
              <div style={S.formGroup}>
                <label style={S.formLabel}>Author Avatar (optional)</label>
                <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={pickAvatar} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#f5f4f0', border: '2px dashed #e0ddd8', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    onClick={() => avatarInputRef.current?.click()}
                  >
                    {avatarPreview
                      ? <img src={avatarPreview} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <User size={20} color="#ccc" />
                    }
                  </div>
                  <button
                    onClick={() => avatarInputRef.current?.click()}
                    style={{ fontSize: '12px', color: '#ff5a00', background: 'rgba(255,90,0,0.07)', border: '1px solid rgba(255,90,0,0.2)', borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', fontFamily: "'Satoshi', sans-serif" }}
                  >
                    {avatarPreview ? 'Change Photo' : 'Upload Photo'}
                  </button>
                  {avatarPreview && (
                    <button
                      onClick={() => { setForm(f => ({ ...f, author_avatar: '' })); setAvatarPreview(''); }}
                      style={{ fontSize: '12px', color: '#aaa', background: 'none', border: 'none', cursor: 'pointer' }}
                    >Remove</button>
                  )}
                </div>
              </div>

              {/* Description */}
              <div style={S.formGroup}>
                <label style={S.formLabel}>Short Description * <span style={{ color: '#ccc', fontWeight: 400 }}>({300 - (form.description?.length || 0)} left)</span></label>
                <textarea
                  className="bm-form-input"
                  style={{ ...S.formTextarea, minHeight: '80px' }}
                  value={form.description}
                  maxLength={300}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="A brief excerpt shown on the blog listing page..."
                />
              </div>

              {/* Content */}
              <div style={S.formGroup}>
                <label style={S.formLabel}>Full Content *</label>
                <div style={S.contentNotice}>
                  💡 You can write plain text or paste HTML. Line breaks are preserved. Use &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;strong&gt;, &lt;em&gt; tags for rich formatting.
                </div>
                <textarea
                  className="bm-form-input"
                  style={{ ...S.formTextarea, minHeight: '220px' }}
                  value={form.content}
                  onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  placeholder="Write your full article here..."
                />
              </div>

              {/* Tags */}
              <div style={S.formGroup}>
                <label style={S.formLabel}>Tags</label>
                <div style={S.tagsWrap} onClick={() => tagInputRef.current?.focus()}>
                  {form.tags.map(t => (
                    <span key={t} style={S.tagPill}>
                      {t}
                      <button onClick={() => removeTag(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ff5a00', padding: 0, lineHeight: 1, fontSize: '13px' }}>×</button>
                    </span>
                  ))}
                  <input
                    ref={tagInputRef}
                    style={S.tagInput}
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={handleTagKey}
                    onBlur={addTag}
                    placeholder={form.tags.length === 0 ? 'Type a tag and press Enter...' : ''}
                  />
                </div>
                <p style={{ fontSize: '11px', color: '#ccc', marginTop: '4px' }}>Press Enter or comma to add a tag</p>
              </div>

              {/* Slug (optional) */}
              <div style={S.formGroup}>
                <label style={S.formLabel}>Custom Slug <span style={{ color: '#ccc', fontWeight: 400 }}>(auto-generated if empty)</span></label>
                <input
                  className="bm-form-input"
                  style={S.formInput}
                  value={form.slug}
                  onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '') }))}
                  placeholder="e.g. how-to-sell-your-car"
                />
              </div>

              {/* Publish toggle */}
              <div style={S.formGroup}>
                <label style={S.formLabel}>Visibility</label>
                <button
                  style={S.publishToggle(form.published)}
                  onClick={() => setForm(f => ({ ...f, published: !f.published }))}
                >
                  <div style={S.toggleDot(form.published)} />
                  {form.published ? 'Published — visible to all visitors' : 'Draft — hidden from public'}
                </button>
              </div>

              {/* Save */}
              <button
                className="bm-save"
                style={S.saveBtn}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <Loader2 size={17} style={{ animation: 'spin 1s linear infinite' }} /> : <FileText size={17} />}
                {saving ? 'Saving...' : editBlog ? 'Update Article' : 'Publish Article'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogsManager;